import { useCallback, useEffect, useMemo, useState } from 'react'
import { adminApiClient } from './adminApiClient'

const cache = new Map()
const inFlight = new Map()
const TTL = 6 * 60 * 60 * 1000
const RETENTION = 24 * 60 * 60 * 1000
const safePeriod = value => [7, 30, 90].includes(Number(value)) ? Number(value) : 30
const keyFor = period => `growth:v2:${safePeriod(period)}`
const listeners = new Set()
const notify = () => listeners.forEach(listener => listener())
const requestKeyFor = (period, refresh) => `${keyFor(period)}:${refresh ? 'refresh' : 'load'}`
const hasInFlight = period => inFlight.has(requestKeyFor(period, false)) || inFlight.has(requestKeyFor(period, true))
const hasRefreshInFlight = period => inFlight.has(requestKeyFor(period, true))

function start(period, refresh = false) {
  const key = keyFor(period)
  const requestKey = requestKeyFor(period, refresh)
  if (inFlight.has(requestKey)) return inFlight.get(requestKey)
  const promise = adminApiClient.get('/api/admin/growth', { params: { period: safePeriod(period), ...(refresh ? { refresh: 1 } : {}) } }).then(({ data: body }) => {
    if (!['ok', 'partial'].includes(body.status) || body.contractVersion !== 2) {
      const error = new Error('growth_partial')
      error.code = 'growth_partial'
      throw error
    }
    cache.set(key, { data: body, updatedAt: Date.now(), fetchedAt: body.fetchedAt || null, error: null, status: 'ready' }); notify(); return body
  }).catch(error => {
    // Persist the failure even without a cached entry: otherwise the page
    // silently renders only its header instead of showing an error state.
    const entry = cache.get(key)
    if (entry) { entry.error = error; notify() } else { cache.set(key, { data: null, updatedAt: 0, fetchedAt: null, error, status: 'error' }); notify() }
    throw error
  }).finally(() => inFlight.delete(requestKey))
  inFlight.set(requestKey, promise); notify(); return promise
}

export function clearGrowthCache() { cache.clear(); inFlight.clear(); notify() }
export function fetchGrowth(period = 30, { refresh = false } = {}) {
  const key = keyFor(period); const entry = cache.get(key)
  if (!refresh && entry && Date.now() - entry.updatedAt < RETENTION) {
    if (Date.now() - entry.updatedAt >= TTL) start(period).catch(() => null)
    return Promise.resolve(entry.data)
  }
  return start(period, refresh)
}
export function useGrowth(period = 30) {
  const safe = safePeriod(period)
  const read = useCallback(() => { const entry = cache.get(keyFor(safe)); return { data: entry?.data || null, error: entry?.error || null, updatedAt: entry?.updatedAt || null, loading: !entry?.data && hasInFlight(safe), refreshing: hasRefreshInFlight(safe) } }, [safe])
  // A cached entry may be rendered before the first request settles. Keep the
  // initial view in a loading state so source-specific consumers do not treat
  // that cache as the latest verified response.
  const [state, setState] = useState(() => ({ ...read(), loading: true }))
  useEffect(() => { const sync = () => setState(read()); listeners.add(sync); setState(read()); fetchGrowth(safe).catch(() => setState(read())); return () => listeners.delete(sync) }, [safe, read])
  const refresh = useCallback(() => { setState(current => ({ ...current, refreshing: true, error: null })); return fetchGrowth(safe, { refresh: true }).catch(error => { setState(read()); throw error }) }, [safe, read])
  return useMemo(() => ({ ...state, refresh }), [state, refresh])
}
